/**
 * App.js — Phase 5 update (Multi-Tenant SaaS)
 *
 * Key changes from single-tenant version:
 *
 * 1. WholesalerGate (new)
 *    Guards all retailer-facing routes. If a logged-in user has no wholesalerId
 *    in their token (not yet joined a wholesaler) they are redirected to /join.
 *    Role-based admins bypass the gate (wholesaler admins have a wholesalerId;
 *    platform admins have their own panel).
 *
 * 2. /join route (new)
 *    Lazy-loaded WholesalerJoin page. Handles wholesaler search, invite code
 *    entry, and post-join token refresh. Phase 6 will expand this page.
 *

 * 4. Per-tenant approval gate (replaces global appConfig)
 *    approvalEnabled now comes from useWholesaler() which reads
 *    wholesalers/{wholesalerId}/settings.retailerApprovalEnabled via a live
 *    onSnapshot. The backend's POST /config/retailer-approval already writes
 *    to BOTH the wholesaler settings document AND the legacy appConfig, so
 *    Admin.js's toggle continues to work without any Admin.js changes.
 *    The old appConfig/retailerApproval listener is removed — WholesalerContext
 *    provides the same data with tenant isolation built in.
 *
 * 5. Loading guard update
 *    Replaced the old configLoaded state (waiting for appConfig to load) with
 *    a check on wholesalerLoading — only blocks rendering for logged-in users
 *    with a wholesalerId while the wholesaler doc is fetched. Non-logged-in
 *    users see the login page immediately with zero wait.
 */
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { useWholesaler } from "./context/WholesalerContext";
import ProtectedAdminRoute from "./context/ProtectedAdminRoute";
import theme from "./theme";
import ShopProfileForm from "./pages/ShopProfileForm";
import Maintenance from "./pages/Maintenance";
import PwaInstallPrompt from "./components/PwaInstallPrompt";
import { ProductsProvider } from "./context/ProductsContext"; // M3: admin-mode provider

/* ---------------- LAZY PAGE IMPORTS ---------------- */
const Login          = lazy(() => import("./pages/Login"));
const Dashboard      = lazy(() => import("./pages/Dashboard"));
const Products       = lazy(() => import("./pages/Products"));
const Cart           = lazy(() => import("./pages/Cart"));
const Orders         = lazy(() => import("./pages/Orders"));
const OrderDetails   = lazy(() => import("./pages/OrderDetails"));
const OrderSuccess   = lazy(() => import("./pages/OrderSuccess"));
const Returns        = lazy(() => import("./pages/Returns"));
const Profile        = lazy(() => import("./pages/Profile"));
const Notifications  = lazy(() => import("./pages/Notifications"));
const Admin          = lazy(() => import("./pages/Admin"));
const AboutUs        = lazy(() => import("./pages/AboutUs"));
const PrivacyPolicy  = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const ReturnPolicy   = lazy(() => import("./pages/ReturnPolicy"));
const ShippingPolicy = lazy(() => import("./pages/ShippingPolicy"));
const GrievanceRedressal = lazy(() => import("./pages/GrievanceRedressal"));
const RewardsTracker = lazy(() => import("./pages/RewardsTracker"));
const WholesalerJoin  = lazy(() => import("./pages/WholesalerJoin"));  // Phase 5

const PLATFORM_ADMIN_URL = (
  process.env.REACT_APP_PLATFORM_ADMIN_URL || "https://admin.urbamart.in"
).replace(/\/+$/, "");


const PageLoader = () => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "center",
    height: "100dvh", background: theme.colors.bg,
  }}>
    <div className="skeleton" style={{ width: 48, height: 48, borderRadius: "50%" }} />
  </div>
);

function PlatformAdminRedirect() {
  useEffect(() => {
    window.location.replace(`${PLATFORM_ADMIN_URL}/login`);
  }, []);

  return <PageLoader />;
}

/* ── Pending approval screen ── */
function PendingApproval() {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return <ShopProfileForm onSubmitted={() => setEditing(false)} />;
  }

  return (
    <div style={{
      minHeight: "100dvh", background: theme.colors.bg,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div style={{
        background: "white", borderRadius: theme.radius.lg,
        padding: 28, maxWidth: 420, width: "100%", textAlign: "center",
        boxShadow: theme.shadow?.card,
      }}>
        <p style={{ fontSize: 42, margin: "0 0 12px" }}>⏳</p>
        <h2 style={{ margin: "0 0 8px", fontWeight: 800, fontSize: 20 }}>Verification in Progress</h2>
        <p style={{ fontSize: 13, color: theme.colors.textMid, margin: "0 0 16px", lineHeight: 1.5 }}>
          Our team is verifying your shop details. You'll be notified as soon as ordering is unlocked.
        </p>
        
        {/* Zero WhatsApp exposure security notice */}
        <div style={{
          background: "#F8FAFC", border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.radius.md, padding: "10px 14px", marginBottom: 18,
          fontSize: 11, color: theme.colors.textLight, lineHeight: 1.4,
        }}>
          🛡️ For security and spam prevention, direct chat links are enabled only after account verification.
        </div>

        <button
          onClick={() => setEditing(true)}
          style={{
            width: "100%", padding: "11px 16px", borderRadius: theme.radius.md,
            border: `1.5px solid ${theme.colors.primary}`, background: "white",
            color: theme.colors.primary, fontWeight: 700, fontSize: 13,
            cursor: "pointer", transition: "all 0.15s ease",
          }}
        >
          ✏️ Update Details / Attach Shop Photos
        </button>
      </div>
    </div>
  );
}

/* ── Rejected screen ── */
function RejectedScreen() {
  return (
    <div style={{
      minHeight: "100dvh", background: theme.colors.bg,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div style={{
        background: "white", borderRadius: theme.radius.lg,
        padding: 28, maxWidth: 380, width: "100%", textAlign: "center",
        boxShadow: theme.shadow?.card,
      }}>
        <p style={{ fontSize: 40, margin: "0 0 12px" }}>❌</p>
        <h2 style={{ margin: "0 0 8px", fontWeight: 800, fontSize: 20 }}>Not Approved</h2>
        <p style={{ fontSize: 13, color: theme.colors.textMid, margin: "0 0 20px", lineHeight: 1.5 }}>
          Your shop registration was not approved. Please contact us for more information.
        </p>
      </div>
    </div>
  );
}


/* ---------------- PROTECTED ROUTE ---------------- */
function ProtectedRoute({ children }) {
  const { isLoggedIn, loading } = useAuth();
  if (loading) return null;
  return isLoggedIn ? children : <Navigate to="/" />;
}

/* ---------------- WHOLESALER GATE (Phase 5) ---------------- */
// Guards all retailer-facing routes. A logged-in user without a wholesalerId
// (hasn't joined a wholesaler yet) is redirected to /join.
//
// Bypass cases:
//   1. platform_admin — global access, no wholesalerId by design
//   2. isAdmin (wholesaler_admin) — has wholesalerId, will pass the !wholesalerId
//      check below anyway, but short-circuit here is cleaner.
function WholesalerGate({ children }) {
  const { isLoggedIn, loading, wholesalerId, userRole, isAdmin } = useAuth();
  const { loading: wholesalerLoading } = useWholesaler();

  if (loading || wholesalerLoading) return <PageLoader />;
  if (!isLoggedIn) return <Navigate to="/" />;

  // Platform admins have their own panel — no wholesalerId needed
  if (userRole === "platform_admin") return children;

  // Role-based admins bypass the gate.
  // Wholesaler admins have wholesalerId and would pass the check below anyway.
  if (isAdmin) return children;

  // Retailer without a wholesalerId — needs to join a wholesaler first
  if (!wholesalerId) return <Navigate to="/join" replace />;

  return children;
}

/* ── Route that requires retailer approval for commerce actions ── */
function OrderingRoute({ children }) {
  const { isLoggedIn, loading, shopProfile, isAdmin } = useAuth();
  const { wholesaler } = useWholesaler();
  if (loading || shopProfile === undefined) return <PageLoader />;
  if (!isLoggedIn) return <Navigate to="/" />;

  // Admins bypass everything
  if (isAdmin) return children;

  const approvalEnabled = wholesaler?.settings?.retailerApprovalEnabled ?? false;

  if (!shopProfile) return <ShopProfileForm />;
  const approvalStatus = shopProfile.approvalStatus
    || (shopProfile.approved === true || (shopProfile.status === "approved" && shopProfile.orderingApproved) ? "approved" : shopProfile.status || "pending");
  if (approvalStatus === "rejected") return <RejectedScreen />;
  if (!approvalEnabled || approvalStatus === "approved") return children;
  return <PendingApproval />;
}

/* ---------------- APP ---------------- */
function App() {
  const { isLoggedIn, userRole } = useAuth();

  // Block rendering only for logged-in users while their wholesaler config loads.
  // Non-logged-in users see the login page immediately — no wait.
  // (WholesalerContext sets loading=false immediately when there's no wholesalerId.)
  const maintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === "true";

  if (maintenanceMode) return <Maintenance />;
  // Platform admins belong to the standalone admin app. Keeping them out of
  // the customer route tree also prevents retailer-only API calls that require
  // a wholesalerId (platform admins intentionally do not have one).
  if (isLoggedIn && userRole === "platform_admin") return <PlatformAdminRedirect />;

  return (
    <div
      className="app-container"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
        minHeight: "100dvh",
        boxSizing: "border-box",
      }}
    >
      <BrowserRouter>
        <PwaInstallPrompt />
        <Suspense fallback={<PageLoader />}>
          <Routes>

            {/* LOGIN */}
            <Route
              path="/"
              element={isLoggedIn ? <Navigate to="/dashboard" /> : <Login />}
            />

            {/* WHOLESALER JOIN — Phase 5: retailers land here if no wholesalerId */}
            <Route
              path="/join"
              element={
                <ProtectedRoute>
                  <WholesalerJoin />
                </ProtectedRoute>
              }
            />

            {/* SHOP REGISTRATION ROUTE */}
            <Route
              path="/register-shop"
              element={
                <WholesalerGate>
                  <ProtectedRoute>
                    <ShopProfileForm onSubmitted={() => window.location.href = "/products"} />
                  </ProtectedRoute>
                </WholesalerGate>
              }
            />


            {/* USER ROUTES — gated by WholesalerGate + ProtectedRoute */}
            <Route path="/dashboard"     element={<WholesalerGate><ProtectedRoute><Dashboard /></ProtectedRoute></WholesalerGate>} />
            <Route path="/orders"        element={<WholesalerGate><ProtectedRoute><Orders /></ProtectedRoute></WholesalerGate>} />
            <Route path="/order/:id"     element={<WholesalerGate><ProtectedRoute><OrderDetails /></ProtectedRoute></WholesalerGate>} />
            <Route path="/returns"       element={<WholesalerGate><ProtectedRoute><Returns /></ProtectedRoute></WholesalerGate>} />
            <Route path="/profile"       element={<WholesalerGate><ProtectedRoute><Profile /></ProtectedRoute></WholesalerGate>} />
            <Route path="/notifications" element={<WholesalerGate><ProtectedRoute><Notifications /></ProtectedRoute></WholesalerGate>} />
            <Route path="/rewards"       element={<WholesalerGate><ProtectedRoute><RewardsTracker /></ProtectedRoute></WholesalerGate>} />

            {/* ORDERING ROUTES — gated by WholesalerGate + approval when ON */}
            <Route path="/products"
              element={
                <WholesalerGate>
                  <ProtectedRoute>
                    <Products />
                  </ProtectedRoute>
                </WholesalerGate>
              }
            />
            <Route path="/cart"
              element={
                <WholesalerGate>
                  <OrderingRoute>
                    <Cart />
                  </OrderingRoute>
                </WholesalerGate>
              }
            />
            <Route path="/success"
              element={
                <WholesalerGate>
                  <OrderingRoute>
                    <OrderSuccess />
                  </OrderingRoute>
                </WholesalerGate>
              }
            />

            {/* ADMIN ROUTE — M3: wrapped in its own ProductsProvider with admin=true.
                Mounts a full live onSnapshot only when admin is on /admin.
                Unsubscribes automatically when admin navigates away.
                Phase 5: ProductsProvider is now tenant-scoped; products are filtered
                to the wholesaler admin's own wholesalerId. */}
            <Route
              path="/admin"
              element={
                <ProtectedAdminRoute>
                  <ProductsProvider admin={true}>
                    <Admin />
                  </ProductsProvider>
                </ProtectedAdminRoute>
              }
            />

            {/* LEGAL PAGES — public */}
            <Route path="/privacy-policy"   element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/about"            element={<AboutUs />} />
            <Route path="/return-policy"    element={<ReturnPolicy />} />
            <Route path="/shipping-policy"  element={<ShippingPolicy />} />
            <Route path="/grievance-redressal" element={<GrievanceRedressal />} />

            {/* FALLBACK */}
            <Route path="*" element={<Navigate to="/" />} />

          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
